import type { ProposalOperationReceipt } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalChangeFieldLabel } from "work/proposals/negotiation/proposal-change-description";

export type ConfirmationMode = "review" | "save";

/**
 * S2 (ROADMAP.md, "a silent drop is a bug; make it loud"): the semantic merge never rejects a save
 * for a content conflict — a losing operation simply does not apply, last-write-wins — so a save
 * that returns 200 can still have left part of what the actor typed un-persisted. Returns undefined
 * when every submitted operation applied (the historical, silent-success case); otherwise a message
 * naming each affected field, in the one shared change vocabulary (`proposal-change-description.ts`),
 * so a partial success is never indistinguishable from a full one.
 */
export function getProposalOperationDropWarning(
  operationReceipts: readonly ProposalOperationReceipt[] | undefined,
): string | undefined {
  const dropped = (operationReceipts ?? []).filter((receipt) => !receipt.applied);
  if (dropped.length === 0) return undefined;

  const fieldLabels = [...new Set(dropped.map((receipt) => proposalChangeFieldLabel(receipt.path)))];
  const fieldList =
    fieldLabels.length <= 2
      ? fieldLabels.join(" and ")
      : `${fieldLabels.slice(0, -1).join(", ")}, and ${fieldLabels.at(-1)}`;

  return `Some of what you saved did not apply — it changed on the other side first. Reload to see the latest ${fieldList}.`;
}

export function getProposalSaveSuccessMessage(args: {
  confirmationMode: ConfirmationMode;
  hasReviewerOnlyUpdates: boolean;
}) {
  const { confirmationMode, hasReviewerOnlyUpdates } = args;

  if (!hasReviewerOnlyUpdates) return "Proposal changes saved.";
  if (confirmationMode === "save") return "Proposal reviewers saved";

  return "Proposal reviewers updated";
}

/** What a failed save was doing, as it reads after "Something went wrong" (#6614). */
export function getProposalSaveFailureDoing(args: {
  confirmationMode: ConfirmationMode;
  hasReviewerOnlyUpdates: boolean;
}) {
  const { confirmationMode, hasReviewerOnlyUpdates } = args;

  if (!hasReviewerOnlyUpdates) return "saving your proposal changes";
  if (confirmationMode === "save") return "saving the proposal reviewers";

  return "updating the proposal reviewers";
}

type SaveConfirmationCopyArgs = {
  confirmationMode: ConfirmationMode;
  hasPendingChanges: boolean;
  hasReviewerChanges: boolean;
  hasReviewerContext: boolean;
  hasReviewerRecipients: boolean;
  proposalIsSaved: boolean;
  reviewerConfirmMessage: string;
  reviewerConfirmOkButtonText: string;
  reviewerConfirmSupplementalMessage?: string;
  reviewerConfirmTitle: string;
  /** Readable change log (field label + value change) for the content being saved. */
  changeLogLines?: string[];
  /**
   * How many changes the dialog ITEMIZES beside this message, split the way the message's clauses are.
   * When the dialog renders that list, this message must not also summarise it: saying "this will save
   * all pending changes" above a list of the actual changes states the same thing twice, once uselessly.
   *
   * Split, because a clause is only redundant if the list truly carries it — the reviewer clause and the
   * reviewer descriptors come from different sources and can disagree.
   */
  describedChangeCount?: number;
  describedReviewerChangeCount?: number;
  /** Who receives what this save persists — "your reviewers", "your team". */
  audienceLabel?: string;
};

export function getSaveConfirmationCopy(args: SaveConfirmationCopyArgs) {
  const copy = buildSaveConfirmationCopy(args);
  // #4952: a save with nothing to diff against — no prior persisted version of this draft — has
  // nothing to itemize. Listing every field the actor just typed as a "change" restates their own
  // input back at them; the plain confirmation is all this save has to say.
  const showsChangeSummary = !isInitialProposalSave(args);
  return {
    ...copy,
    changeLogHeading: proposalSaveChangeLogHeading(args),
    // Changes only. A comment reaches its audience as it is posted (see
    // `proposal-comment-publishing.ts`), so a save has nothing to say about one.
    message: showsChangeSummary
      ? appendBulletedSection(copy.message, proposalSaveChangeLogHeading(args), args.changeLogLines)
      : copy.message,
    showsChangeSummary,
  };
}

/**
 * True on a draft's very first save: no prior persisted version exists to diff against, so an
 * itemized change list would only repeat what the actor just typed rather than describe a change
 * (#4952). Every later save — even one that immediately follows creation — has a real prior version
 * once this one persists, and keeps its summary.
 *
 * Proposal state, not a UI flag: `proposalIsSaved` already answers "does a persisted version of this
 * proposal exist" for the confirmation title, so the same signal answers this.
 */
export function isInitialProposalSave(args: { proposalIsSaved: boolean }): boolean {
  return !args.proposalIsSaved;
}

/**
 * Names both what is being saved and who it reaches, so the list below it is unambiguous. Exported so
 * the dialog can head a RENDERED change list with the same words the text form uses — the heading is
 * the wording, and it must not exist twice.
 */
export function proposalSaveChangeLogHeading(args: {
  audienceLabel?: string;
}): string {
  return args.audienceLabel ? `Changes being saved, shared with ${args.audienceLabel}` : "Changes being saved";
}

/**
 * The one bulleted-section helper. The save and submit confirmations render the same shape, and
 * writing it twice is how their wording drifted apart in the first place.
 */
export function appendBulletedSection(message: string, heading: string, lines?: readonly string[]): string {
  if (!lines || lines.length === 0) return message;
  const list = lines.map((line) => `• ${line}`).join("\n");
  const section = `${heading}:\n${list}`;
  // A message the itemized list has made redundant is empty, not blank-padded — leading whitespace above
  // the first section would render as a gap where a sentence used to be.
  return message ? `${message}\n\n${section}` : section;
}

function buildSaveConfirmationCopy(args: SaveConfirmationCopyArgs) {
  const {
    confirmationMode,
    hasPendingChanges,
    hasReviewerChanges,
    hasReviewerContext,
    hasReviewerRecipients,
    proposalIsSaved,
    reviewerConfirmMessage,
    reviewerConfirmOkButtonText,
    reviewerConfirmSupplementalMessage,
    reviewerConfirmTitle,
  } = args;

  const hasPendingContentUpdates = hasPendingChanges;
  // The itemized list names every content change and reviewer change; a sentence that also announces
  // them adds nothing and contradicts nothing, it just makes the dialog say it twice. Comments used to
  // be the exception, because staged comments were carried by the save without ever appearing in that
  // list — they are not carried by it at all now, so the save says nothing about them.
  const changesAreItemized = (args.describedChangeCount ?? 0) > 0;
  const reviewerChangesAreItemized = (args.describedReviewerChangeCount ?? 0) > 0;
  const hasDescribedChanges = changesAreItemized || reviewerChangesAreItemized;
  const unlistedItems = (reviewerChangeLabel: string) => [
    hasPendingChanges && !changesAreItemized ? "changes" : undefined,
    hasReviewerChanges && !reviewerChangesAreItemized ? reviewerChangeLabel : undefined,
  ];
  const hasUnlistedItems = unlistedItems("reviewer changes").some(Boolean);
  const reviewerConfirmSupplement = reviewerConfirmSupplementalMessage
    ? ` ${reviewerConfirmSupplementalMessage}`
    : "";
  const saveTitle = getSaveConfirmationTitle({
    hasPendingChanges,
    hasReviewerChanges,
    proposalIsSaved,
  });

  if (confirmationMode === "review" && hasReviewerContext) {
    if (hasPendingContentUpdates) {
      // "For review" is what this mode adds over an ordinary save, so it survives even when the list has
      // taken over naming the changes — the sentence then points at the list instead of restating it.
      const reviewSubject = hasUnlistedItems
        ? `all pending ${formatConfirmationItems(unlistedItems("reviewer updates"))}`
        : "these changes";
      return {
        title: hasReviewerChanges ? reviewerConfirmTitle : saveTitle,
        message: `This will save ${reviewSubject} for review.${reviewerConfirmSupplement}`,
        okButtonText: reviewerConfirmOkButtonText,
      };
    }

    return {
      title: reviewerConfirmTitle,
      message: `${reviewerConfirmMessage}${reviewerConfirmSupplement}`,
      okButtonText: reviewerConfirmOkButtonText,
    };
  }

  if (hasPendingContentUpdates || hasReviewerChanges) {
    // With the list carrying everything, the sentence is dropped outright rather than reduced to a
    // vaguer echo of it: the list's own heading already names both what is saved and who receives it,
    // which is all this sentence ever said.
    const message = hasUnlistedItems
      ? `This will save ${hasReviewerChanges && !hasPendingContentUpdates ? "" : "all pending "}${formatConfirmationItems(
          unlistedItems("reviewer changes"),
        )}.${hasReviewerRecipients && hasPendingContentUpdates && !hasDescribedChanges ? " Reviewers will receive the updates." : ""}`
      : "";

    return {
      title: saveTitle,
      // The supplement is written to follow a sentence, so it carries a leading space; with the sentence
      // gone it would open the dialog with one.
      message: `${message}${reviewerConfirmSupplement}`.trim(),
      okButtonText: "Save",
    };
  }

  return {
    title: saveTitle,
    message: proposalIsSaved ? "This will save the proposal." : "This will save the draft proposal.",
    okButtonText: "Save",
  };
}

/**
 * The dialog used to have comment-bearing titles — "Save Comments?", "Save Changes and Comments?" —
 * because a save was partly about the actor's held comments. It is about the proposal's changes and the
 * reviewers they go to, and nothing else.
 */
function getSaveConfirmationTitle(args: {
  hasPendingChanges: boolean;
  hasReviewerChanges: boolean;
  proposalIsSaved: boolean;
}) {
  const { hasPendingChanges, hasReviewerChanges, proposalIsSaved } = args;

  if (hasReviewerChanges && hasPendingChanges) return "Save Updates?";
  if (hasReviewerChanges) return "Save Reviewer Changes?";
  if (hasPendingChanges) return "Save Changes?";
  return proposalIsSaved ? "Save Proposal?" : "Save Draft Proposal?";
}

function formatConfirmationItems(items: Array<string | undefined>) {
  const definedItems = items.filter((item): item is string => Boolean(item));

  if (definedItems.length <= 1) return definedItems[0] ?? "updates";
  if (definedItems.length === 2) return `${definedItems[0]} and ${definedItems[1]}`;

  return `${definedItems.slice(0, -1).join(", ")}, and ${definedItems.at(-1)}`;
}
